import { Button, Card, Divider, Flex, Text, View, useTheme } from "@aws-amplify/ui-react";
import { StorageImage } from "@aws-amplify/ui-react-storage";
import { AuthUser } from "aws-amplify/auth";
import { ListPaginateWithPathOutput } from "aws-amplify/storage";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IoNavigate, IoShareSocial } from "react-icons/io5";
import I18nTextUtils from "../utils/i18n/I18nTextUtils";
import InputSanitizationUtils from "../utils/InputSanitizationUtils";
import PlaceDataUtils, { NewRatingDefinition, PlaceDefinition, RatingDefinition, RatingStatus } from "../utils/PlaceDataUtils";
import PublicUserProfileUtils, { PublicUserProfile } from "../utils/PublicUserProfileUtils";
import StorageUtils from "../utils/StorageUtils";
import { showNotification } from "../utils/ui/NotificationUtils";
import { EnvironmentUtils } from "../utils/EnvironmentUtils";
import ClientDeviceUtils from "../utils/client/ClientDeviceUtils";
import ImageGallery from "./images/ImageGallery";
import PlaceCategoryBadge from "./places/PlaceCategoryBadge";
import PlacePropertyBadge from "./places/PlacePropertyBadge";
import PlaceRating from "./rating/PlaceRating";
import RatingThumbnail from "./rating/RatingThumbnail";
import RatingsList from "./rating/RatingsList";
import UserThumbnail from './user/UserThumbnail';
import PlaceOverviewMap from './PlaceOverviewMap';
import { LatLng } from 'leaflet';

import './PlaceOverviewPopup.css';

interface PlaceOverviewPopupProps {
    placeDefinition: PlaceDefinition;
    loggedInUser: AuthUser | null;
    handleCloseOverlay: () => void;
    handleShowLogin: () => void;
}

const PlaceOverviewPopup = ({ loggedInUser, placeDefinition, handleCloseOverlay, handleShowLogin }: PlaceOverviewPopupProps): JSX.Element => {
    const { t } = useTranslation();
    const { tokens } = useTheme();

    const capitalizeFirstChar = (str: string) => {
        if (!str) return str;
        return str.charAt(0).toUpperCase() + str.slice(1);
    };

    const [creatorPublicProfile, setCreatorPublicProfile] = useState<PublicUserProfile | null>(null);
    const [placeImages, setPlaceImages] = useState<ListPaginateWithPathOutput | null>(null);
    const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
    const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
    const [placeRatings, setPlaceRatings] = useState<RatingDefinition[]>([]);
    const [ratingCreatorProfiles, setRatingCreatorProfiles] = useState<{ [key: string]: PublicUserProfile | null }>({});
    const [rating, setRating] = useState<number>(0);
    const [ratingText, setRatingText] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [existingRating, setExistingRating] = useState<RatingDefinition | null>(null);

    useEffect(() => {
        const updateCreatorPublicProfile = async () => {
            const profile = await PublicUserProfileUtils.getPublicProfileByUserIdFromDb(loggedInUser != null, placeDefinition.creatorUserId || '');
            if (profile) {
                setCreatorPublicProfile(profile);
            } else if (placeDefinition.creatorUserId) {
                // If profile doesn't exist but we have creatorUserId, create a minimal profile object
                // This ensures author information is always visible
                setCreatorPublicProfile({
                    userId: placeDefinition.creatorUserId,
                    userIdentityId: '',
                    nickname: '' // Will show userId as fallback in UserThumbnail
                });
            }
        };

        const loadPlaceImages = async () => {
            const images = await StorageUtils.getPlaceImagesFiles(placeDefinition.placeId);
            setPlaceImages(images);
        };

        const loadPlaceRatings = async () => {
            const ratings = await PlaceDataUtils.getPlaceRatings(placeDefinition.placeId, loggedInUser);
            setPlaceRatings(ratings);

            // Fetch creator profiles for all ratings
            const profiles: { [key: string]: PublicUserProfile | null } = {};
            for (const rating of ratings) {
                const profile = await PublicUserProfileUtils.getPublicProfileByUserIdFromDb(true, rating.creatorUserId);
                profiles[rating.id] = profile;
            }
            setRatingCreatorProfiles(profiles);
        };

        const checkExistingRating = async () => {
            if (!loggedInUser) return;
            const userRating = await PlaceDataUtils.getUserPlaceRating(placeDefinition.placeId, loggedInUser);
            if (userRating) {
                setExistingRating(userRating);
            }
        };

        updateCreatorPublicProfile();
        loadPlaceImages();
        loadPlaceRatings();
        checkExistingRating();
    }, [loggedInUser, placeDefinition.creatorUserId, placeDefinition.placeId]);

    const handleThumbnailClick = (index: number) => {
        setSelectedImageIndex(index);
    };

    const handleMainImageClick = () => {
        setIsFullscreen(true);
    };

    const handleFullscreenClose = () => {
        setIsFullscreen(false);
    };

    const handlePrevImage = () => {
        if (placeImages?.items) {
            setSelectedImageIndex((prev) =>
                prev === 0 ? placeImages.items.length - 1 : prev - 1
            );
        }
    };

    const handleNextImage = () => {
        if (placeImages?.items) {
            setSelectedImageIndex((prev) =>
                prev === placeImages.items.length - 1 ? 0 : prev + 1
            );
        }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
        if (!isFullscreen) return;

        switch (event.key) {
            case 'Escape':
                handleFullscreenClose();
                break;
            case 'ArrowLeft':
                handlePrevImage();
                break;
            case 'ArrowRight':
                handleNextImage();
                break;
        }
    };

    const handleOverlayKeyDown = (event: KeyboardEvent) => {
        // If fullscreen is open, let the fullscreen handler take care of ESC
        if (isFullscreen) return;
        
        if (event.key === 'Escape') {
            handleCloseOverlay();
        }
    };

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isFullscreen]);

    // Handle ESC key to close overlay (when not in fullscreen mode)
    useEffect(() => {
        window.addEventListener('keydown', handleOverlayKeyDown);
        return () => window.removeEventListener('keydown', handleOverlayKeyDown);
    }, [isFullscreen, handleCloseOverlay]);

    const handleTextAreaResize = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        event.target.style.height = 'auto';
        event.target.style.height = event.target.scrollHeight + 'px';
    };

    const handleRatingAdded = async () => {
        const ratings = await PlaceDataUtils.getPlaceRatings(placeDefinition.placeId, loggedInUser);
        setPlaceRatings(ratings);

        // Fetch creator profiles for all ratings
        const profiles: { [key: string]: PublicUserProfile | null } = {};
        for (const rating of ratings) {
            const profile = await PublicUserProfileUtils.getPublicProfileByUserIdFromDb(true, rating.creatorUserId);
            profiles[rating.id] = profile;
        }
        setRatingCreatorProfiles(profiles);
    };

    const handleSubmit = async () => {
        if (!rating || !ratingText || !loggedInUser) return;

        setIsSubmitting(true);
        try {
            const ratingDefinition: NewRatingDefinition = {
                rating,
                ratingText,
                placeId: placeDefinition.placeId,
                creatorUserId: loggedInUser.userId,
                status: RatingStatus.ACTIVE
            };

            await PlaceDataUtils.createNewRating(ratingDefinition, I18nTextUtils.getCurrentLanguageDetail());
            setRating(0);
            setRatingText('');
            handleRatingAdded();
        } catch (error) {
            console.error('Error submitting rating:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const switchToLogin = (): void => {
        handleCloseOverlay();
        handleShowLogin();
    };

    const handleNavigation = (): void => {
        const { lat, lng } = placeDefinition;
        const placeName = I18nTextUtils.resolveInternationalizedText(placeDefinition.name);
        ClientDeviceUtils.handleNavigation(lat, lng, placeName);
    };

    const handleAddressCopy = async (): Promise<void> => {
        const address = capitalizeFirstChar(placeDefinition.addressString || '');
        if (address) {
            try {
                await navigator.clipboard.writeText(address);
                showNotification(t('place.copied'));
            } catch (error) {
                console.error('Failed to copy address to clipboard:', error);
            }
        }
    };

    const handleCoordinatesCopy = async (): Promise<void> => {
        const coordinates = `${placeDefinition.lat.toFixed(6)}, ${placeDefinition.lng.toFixed(6)}`;
        try {
            await navigator.clipboard.writeText(coordinates);
            showNotification(t('place.copied'));
        } catch (error) {
            console.error('Failed to copy coordinates to clipboard:', error);
        }
    };

    const handleShare = async (): Promise<void> => {
        const currentUrl = window.location.href;
        const placeName = I18nTextUtils.resolveInternationalizedText(placeDefinition.name);
        
        // Prepare share data
        const shareData: ShareData = {
            title: placeName,
            text: t('place.share.text', { placeName }),
            url: currentUrl
        };
        
        // Check if Web Share API is available
        // The API requires a secure context (HTTPS or localhost/127.0.0.1)
        // On Android emulator, 10.0.2.2 maps to host's localhost, but is NOT considered secure
        // Solution: Use `adb reverse tcp:5173 tcp:5173` and access via http://localhost:5173
        const isWebShareAvailable = typeof navigator !== 'undefined' && 
                                     navigator.share !== undefined && 
                                     typeof navigator.share === 'function';
        
        // On mobile, try to use Web Share API even if checks suggest it's not available
        // Sometimes the API exists but secure context check fails, yet the call still works
        const shouldTryWebShare = isWebShareAvailable || 
                                  (ClientDeviceUtils.isMobileDevice() && 'share' in navigator);
        
        console.debug('Share attempt:', {
            isMobile: ClientDeviceUtils.isMobileDevice(),
            isWebShareAvailable,
            shouldTryWebShare,
            url: currentUrl,
            isSecureContext: window.isSecureContext,
            hasNavigatorShare: 'share' in (navigator || {}),
            navigatorShareType: typeof (navigator as any).share
        });
        
        // On mobile devices, always try Web Share API first if it might be available
        // Even if isSecureContext is false, we'll attempt the call and catch errors
        if (shouldTryWebShare) {
            // Check if the data can be shared (if canShare is available)
            let canShare = true;
            if (navigator.canShare && typeof navigator.canShare === 'function') {
                try {
                    canShare = navigator.canShare(shareData);
                } catch (error) {
                    console.warn('canShare check failed:', error);
                    canShare = true; // Assume we can share if check fails
                }
            }
            
            if (canShare) {
                try {
                    console.debug('Attempting to use Web Share API');
                    // Double-check that share is actually a function before calling
                    if (typeof navigator.share === 'function') {
                        await navigator.share(shareData);
                        console.debug('Web Share API succeeded');
                        return; // Successfully shared, no need for fallback
                    } else {
                        throw new Error('navigator.share is not a function');
                    }
                } catch (error) {
                    const shareError = error as Error;
                    // User cancelled - don't show error or fallback
                    if (shareError.name === 'AbortError') {
                        console.debug('User cancelled share');
                        return;
                    }
                    // Share failed, log error and fallback to clipboard
                    console.error('Web Share API failed:', shareError.name, shareError.message);
                    // On mobile, if Web Share fails, we should still try clipboard as fallback
                    if (ClientDeviceUtils.isMobileDevice()) {
                        console.warn('Web Share API failed on mobile, falling back to clipboard');
                    }
                }
            } else {
                console.warn('Share data cannot be shared via Web Share API');
            }
        } else {
            // Web Share API not available
            const reason = !window.isSecureContext 
                ? 'Not in secure context (needs HTTPS or localhost)' 
                : 'Browser does not support Web Share API';
            console.warn('Web Share API not available:', reason);
            
            if (ClientDeviceUtils.isMobileDevice()) {
                console.warn('On mobile device but Web Share API unavailable. Possible reasons:', 
                    '1. Not in secure context (needs HTTPS or localhost/127.0.0.1)', 
                    '2. Browser does not support Web Share API',
                    '3. Browser version too old',
                    '4. For Android emulator: try accessing via localhost instead of 10.0.2.2');
            }
        }
        
        // Desktop or fallback: copy to clipboard
        console.debug('Falling back to clipboard copy');
        await handleShareCopyToClipboard(currentUrl);
    };

    const handleShareCopyToClipboard = async (url: string): Promise<void> => {
        // Try modern Clipboard API first
        if (navigator.clipboard && navigator.clipboard.writeText) {
            try {
                await navigator.clipboard.writeText(url);
                showNotification(t('place.share.copied'));
                return;
            } catch (error) {
                console.error('Clipboard API failed:', error);
                // Fall through to legacy method
            }
        }
        
        // Fallback to legacy execCommand method
        try {
            const textArea = document.createElement('textarea');
            textArea.value = url;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            textArea.style.top = '-999999px';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            
            const successful = document.execCommand('copy');
            document.body.removeChild(textArea);
            
            if (successful) {
                showNotification(t('place.share.copied'));
            } else {
                throw new Error('execCommand copy failed');
            }
        } catch (error) {
            console.error('Failed to copy link to clipboard:', error);
            showNotification(t('place.share.failed'));
        }
    };



    return (
        <>
            <View
                className="overlay-content"
                backgroundColor={tokens.colors.background.secondary}
            >
                {/* Fixed Top Bar */}
                <View className="place-overview-top-bar">
                    <Flex
                        className="place-overview-top-bar-content"
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={tokens.space.medium}
                        wrap="wrap"
                    >
                        <Flex
                            className="place-overview-top-bar-right"
                            direction="row"
                            alignItems="center"
                            gap={tokens.space.small}
                        >
                            <Button
                                className="place-overview-share-button"
                                variation="link"
                                size="large"
                                onClick={handleShare}
                                title={t('place.share.title')}
                            >
                                <IoShareSocial size={20} />
                            </Button>
                            <Button
                                className="place-overview-close-button"
                                variation="link"
                                size="large"
                                onClick={handleCloseOverlay}
                            >
                                ✕
                            </Button>
                        </Flex>
                        <Flex
                            className="place-overview-top-bar-left"
                            direction="row"
                            alignItems="center"
                            gap={tokens.space.medium}
                            wrap="wrap"
                            flex="1"
                        >
                            {placeDefinition.placeCategories.map((categoryEnum: string) => (
                                <PlaceCategoryBadge
                                    key={categoryEnum}
                                    categoryEnum={categoryEnum}
                                />
                            ))}
                            <Flex className="place-name-container">
                                <Text className="marker-tooltip-place-name">
                                    {I18nTextUtils.resolveInternationalizedText(placeDefinition.name)}
                                </Text>
                            </Flex>
                        </Flex>
                    </Flex>
                </View>

                <View className="overlay-content-scrollable"
                    padding={tokens.space.medium}
                >
                {/* TODO externalize this part to separate component */}
                {/* Debug Information - Only shown when SHOW_DEBUG_INAPP_DIALOGS environment variable is set to 'true' */}
                {EnvironmentUtils.shouldShowDebugDialogs() && (
                    <Card
                        className="debug-info-container"
                        variation="elevated"
                        marginBottom={tokens.space.medium}
                    >
                        <Flex direction="column" gap={tokens.space.small}>
                            <Text
                                className="debug-title"
                                fontWeight="bold"
                                fontSize={tokens.fontSizes.small}
                                color={tokens.colors.font.secondary}
                            >
                                {t('debug.title')}
                            </Text>
                            <Flex direction="row" alignItems="center" gap={tokens.space.small}>
                                <Text
                                    className="debug-label"
                                    fontSize={tokens.fontSizes.small}
                                    color={tokens.colors.font.secondary}
                                >
                                    {t('debug.is-controlled-with-mouse')}:
                                </Text>
                                <Text
                                    className="debug-value"
                                    fontSize={tokens.fontSizes.small}
                                    fontWeight="bold"
                                    color={!ClientDeviceUtils.isTouchControlUsed() ? tokens.colors.font.success : tokens.colors.font.error}
                                >
                                    {!ClientDeviceUtils.isTouchControlUsed() ? 'true' : 'false'}
                                </Text>
                            </Flex>
                            <Flex direction="row" alignItems="center" gap={tokens.space.small}>
                                <Text
                                    className="debug-label"
                                    fontSize={tokens.fontSizes.small}
                                    color={tokens.colors.font.secondary}
                                >
                                    Touch supported:
                                </Text>
                                <Text
                                    className="debug-value"
                                    fontSize={tokens.fontSizes.small}
                                    fontWeight="bold"
                                    color={ClientDeviceUtils.isTouchSupported() ? tokens.colors.font.success : tokens.colors.font.error}
                                >
                                    {ClientDeviceUtils.isTouchSupported() ? 'true' : 'false'}
                                </Text>
                            </Flex>
                            <Flex direction="row" alignItems="center" gap={tokens.space.small}>
                                <Text
                                    className="debug-label"
                                    fontSize={tokens.fontSizes.small}
                                    color={tokens.colors.font.secondary}
                                >
                                    Last event from touchscreen:
                                </Text>
                                <Text
                                    className="debug-value"
                                    fontSize={tokens.fontSizes.small}
                                    fontWeight="bold"
                                    color={ClientDeviceUtils.getWasLastDownEventFromTouchscreen() === true ? tokens.colors.font.success : 
                                           ClientDeviceUtils.getWasLastDownEventFromTouchscreen() === false ? tokens.colors.font.error : 
                                           tokens.colors.font.secondary}
                                >
                                    {ClientDeviceUtils.getWasLastDownEventFromTouchscreen() === true ? 'true' : 
                                     ClientDeviceUtils.getWasLastDownEventFromTouchscreen() === false ? 'false' : 
                                     'undefined'}
                                </Text>
                            </Flex>
                            <Flex direction="row" alignItems="center" gap={tokens.space.small}>
                                <Text
                                    className="debug-label"
                                    fontSize={tokens.fontSizes.small}
                                    color={tokens.colors.font.secondary}
                                >
                                    Touch control used:
                                </Text>
                                <Text
                                    className="debug-value"
                                    fontSize={tokens.fontSizes.small}
                                    fontWeight="bold"
                                    color={ClientDeviceUtils.isTouchControlUsed() ? tokens.colors.font.success : tokens.colors.font.error}
                                >
                                    {ClientDeviceUtils.isTouchControlUsed() ? 'true' : 'false'}
                                </Text>
                            </Flex>
                            <Flex direction="row" alignItems="center" gap={tokens.space.small}>
                                <Text
                                    className="debug-label"
                                    fontSize={tokens.fontSizes.small}
                                    color={tokens.colors.font.secondary}
                                >
                                    Operating System:
                                </Text>
                                <Text
                                    className="debug-value"
                                    fontSize={tokens.fontSizes.small}
                                    fontWeight="bold"
                                    color={tokens.colors.font.secondary}
                                >
                                    {ClientDeviceUtils.getOperatingSystem()}
                                </Text>
                            </Flex>
                        </Flex>
                    </Card>
                 )}
                <Flex direction="column">
                    <div className="place-overview-image-gallery">
                        <ImageGallery
                            images={placeImages}
                        />
                    </div>

                    <Flex
                        className="place-header-column-flex"
                        direction="column"
                        gap={tokens.space.medium}
                        marginTop={tokens.space.medium}
                    >
                        <Flex
                            className="place-header-row-flex"
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            gap={tokens.space.medium}
                            wrap="wrap"
                        >
                            <Flex
                                className="place-header-row-category-name-flex"
                                direction="row"
                                alignItems="center"
                                gap={tokens.space.medium}
                                wrap="wrap"
                            >
                                {placeDefinition.placeCategories.map((categoryEnum: string) => (
                                    <PlaceCategoryBadge
                                        key={categoryEnum}
                                        categoryEnum={categoryEnum}
                                    />
                                ))}
                                <Flex className="place-name-container">
                                    <Text className="marker-tooltip-place-name">
                                        {I18nTextUtils.resolveInternationalizedText(placeDefinition.name)}
                                    </Text>
                                    {loggedInUser ? (
                                        <>
                                            <Text 
                                                className="place-address clickable-address"
                                                onClick={handleAddressCopy}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                {capitalizeFirstChar(InputSanitizationUtils.sanitizePlainText(placeDefinition.addressString || ''))}
                                            </Text>
                                            <Text 
                                                className="place-coordinates clickable-address"
                                                onClick={handleCoordinatesCopy}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                {placeDefinition.lat.toFixed(6)}, {placeDefinition.lng.toFixed(6)}
                                            </Text>
                                        </>
                                    ) : (
                                        <Text className="place-login-prompt">
                                            {t('place.login-to-see-address')}
                                        </Text>
                                    )}
                                </Flex>
                            </Flex>
                            <Flex
                                direction="row"
                                alignItems="center"
                                gap={tokens.space.small}
                            >
                                {loggedInUser && (
                                    <Button
                                        className="place-navigation-button grey-navigation-button"
                                        variation="primary"
                                        size="small"
                                        onClick={handleNavigation}
                                    >
                                        <IoNavigate 
                                            size={24}
                                            title={!ClientDeviceUtils.isTouchControlUsed() ? t('place.navigation.open-in-maps') : t('place.navigation.navigate')}
                                        />
                                    </Button>
                                )}
                                <RatingThumbnail ratings={placeRatings} />
                            </Flex>
                        </Flex>
                    </Flex>

                    <Card
                        className="place-properties-container"
                    >
                        <Flex
                            direction="row"
                            justifyContent="flex-start"
                            alignContent="flex-start"
                            wrap="wrap"
                            gap={tokens.space.small}
                        >
                            {placeDefinition.placeProperties?.map((propertyEnum: string) => (
                                <PlacePropertyBadge
                                    key={propertyEnum}
                                    propertyEnum={propertyEnum}
                                />
                            ))}
                        </Flex>
                    </Card>

                    <Card
                        className="place-description-container"
                        variation="elevated"
                    >
                        <Text
                            className={`place-description-container ${!I18nTextUtils.resolveInternationalizedText(placeDefinition.description) ? 'no-description' : ''}`}
                            dangerouslySetInnerHTML={{ __html: I18nTextUtils.resolveInternationalizedTextAsHtml(placeDefinition.description) || t('place.creation.no-description') }}
                        />
                    </Card>

                    <Divider
                        orientation="horizontal"
                        size="large"
                        marginTop={tokens.space.large}
                    />

                    <Card
                        className="place-overview-map-card"
                        variation="elevated"
                    >
                        <PlaceOverviewMap
                            placePosition={new LatLng(placeDefinition.lat, placeDefinition.lng)}
                            // isLoggedIn={loggedInUser !== null}
                            isLoggedIn={loggedInUser ? true : false}
                        />
                    </Card>

                    <Card
                        className="ratings-container"
                    >
                        <Card
                            className="enter-rating-container"
                            variation="elevated"
                        >
                            {loggedInUser ? (
                                <PlaceRating
                                    placeId={placeDefinition.placeId}
                                    user={loggedInUser}
                                    onRatingAdded={handleRatingAdded}
                                    onRatingDeleted={handleRatingAdded}
                                />
                            ) : (
                                <Flex direction="column" gap={tokens.space.medium}>
                                    <Text>{t('place.rating.login-required')}</Text>
                                    <Button
                                        variation="primary"
                                        colorTheme="success"
                                        size="large"
                                        loadingText=""
                                        type="submit"
                                        onClick={switchToLogin}
                                    >
                                        {t('user.log-in')}
                                    </Button>
                                </Flex>
                            )}
                        </Card>
                        {loggedInUser && (
                            <>
                                <Divider orientation="horizontal" size="small" marginTop={tokens.space.medium} />
                                <RatingsList
                                    ratings={placeRatings}
                                    ratingCreatorProfiles={ratingCreatorProfiles}
                                    currentUserId={loggedInUser?.userId}
                                />
                            </>
                        )}
                    </Card>

                    <Divider
                        orientation="horizontal"
                        size="large"
                        marginTop={tokens.space.large}
                    />

                    <UserThumbnail userProfile={creatorPublicProfile} currentUserId={loggedInUser?.userId} />
                </Flex>
                </View>
            </View>

            {isFullscreen && placeImages?.items && placeImages.items.length > 0 && (
                <View className="fullscreen-overlay">
                    <View className="fullscreen-image-container">
                        <StorageImage
                            className="fullscreen-image"
                            key={placeImages.items[selectedImageIndex].path}
                            alt=""
                            path={placeImages.items[selectedImageIndex].path}
                        />
                        <Button
                            className="fullscreen-close-button"
                            onClick={handleFullscreenClose}
                        >
                            &times;
                        </Button>
                        <Button
                            className="fullscreen-nav-button prev"
                            onClick={handlePrevImage}
                        >
                            &lt;
                        </Button>
                        <Button
                            className="fullscreen-nav-button next"
                            onClick={handleNextImage}
                        >
                            &gt;
                        </Button>
                    </View>
                </View>
            )}
        </>
    );
};

export default PlaceOverviewPopup;
